import { Box, Tooltip, TooltipProps } from '@mui/material'
import AntIcon from '_icons/AnimalScale/ant.svg?react'
import CowIcon from '_icons/AnimalScale/cow.svg?react'
import DogIcon from '_icons/AnimalScale/dog.svg?react'
import ElephantIcon from '_icons/AnimalScale/elephant.svg?react'
import MouseIcon from '_icons/AnimalScale/mouse.svg?react'
import WhaleIcon from '_icons/AnimalScale/whale.svg?react'
import { LocalizedTypography } from 'components/Localization/LocalizedTypography'
import { ReactElement } from 'react'
import { AnimalScale } from 'services/APIs/InternalAPI/internal-api.contracts'

/**
 * Animal scale classification tooltip metadata.
 * Source spec:
 * Level | Animal | Volume upper bound (m³) | Longest-edge upper bound (m) | Examples
 * 1 Ant       ≤ 8×10⁻⁶ (≈ 8 cm³)          ≤ 0.03 (3 cm)            screws, rivets, pins
 * 2 Mouse     ≤ 7×10⁻³ (≈ 7 L)            ≤ 0.30 (30 cm)           small housings, sensors
 * 3 Dog       ≤ 1.1 (≈ 1,100 L)           ≤ 1.5                    actuators, small machines
 * 4 Cow       ≤ 12.7                      ≤ 3.8                    machine frames, cabinets
 * 5 Elephant  ≤ 106                       ≤ 8.7                    large equipment, full machines
 * 6 Whale     > 106                       > 8.7                    production lines, very large systems
 */
const animalScaleMeta: Partial<
  Record<
    AnimalScale,
    {
      level: number
      animal: string
      volume: string
      edge: string
      examples: string
    }
  >
> = {
  [AnimalScale.Ant]: {
    level: 1,
    animal: 'Ant',
    volume: 'Volume ≤ 8×10⁻⁶m³ (≈ 8cm³)',
    edge: 'Longest edge ≤ 0.03m (3cm)',
    examples: 'screws, rivets, pins',
  },
  [AnimalScale.Mouse]: {
    level: 2,
    animal: 'Mouse',
    volume: 'Volume ≤ 7×10⁻³m³ (≈ 7L)',
    edge: 'Longest edge ≤ 0.30m (30cm)',
    examples: 'small housings, sensors',
  },
  [AnimalScale.Dog]: {
    level: 3,
    animal: 'Dog',
    volume: 'Volume ≤ 1.1m³ (≈ 1,100L)',
    edge: 'Longest edge ≤ 1.5m',
    examples: 'actuators, small machines',
  },
  [AnimalScale.Cow]: {
    level: 4,
    animal: 'Cow',
    volume: 'Volume ≤ 12.7m³',
    edge: 'Longest edge ≤ 3.8m',
    examples: 'machine frames, cabinets',
  },
  [AnimalScale.Elephant]: {
    level: 5,
    animal: 'Elephant',
    volume: 'Volume ≤ 106m³',
    edge: 'Longest edge ≤ 8.7m',
    examples: 'large equipment, full machines',
  },
  [AnimalScale.Whale]: {
    level: 6,
    animal: 'Whale',
    volume: 'Volume > 106m³',
    edge: 'Longest edge > 8.7m',
    examples: 'production lines, very large systems',
  },
}

const iconComponents: Partial<Record<AnimalScale, () => ReactElement | null>> =
  {
    [AnimalScale.Ant]: () => (
      <AntIcon style={{ height: '100%', width: '100%' }} />
    ),
    [AnimalScale.Mouse]: () => (
      <MouseIcon style={{ height: '100%', width: '100%' }} />
    ),
    [AnimalScale.Dog]: () => (
      <DogIcon style={{ height: '100%', width: '100%' }} />
    ),
    [AnimalScale.Cow]: () => (
      <CowIcon style={{ height: '100%', width: '100%' }} />
    ),
    [AnimalScale.Elephant]: () => (
      <ElephantIcon style={{ height: '100%', width: '100%' }} />
    ),
    [AnimalScale.Whale]: () => (
      <WhaleIcon style={{ height: '100%', width: '100%' }} />
    ),
  }

export const AnimalScaleIcon = (props: {
  scale: AnimalScale
  tooltipProps?: Partial<TooltipProps>
}) => {
  const meta = animalScaleMeta[props.scale]
  const Icon = iconComponents[props.scale]
  if (!meta || !Icon) return null

  const titleNode = (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
          <Box
            component="span"
            sx={{
              lineHeight: 0,
              display: 'inline-flex',
              width: '32px',
              alignItems: 'center',
            }}
          >
            <Icon />
          </Box>
        </Box>
      </Box>
      <div>
        <LocalizedTypography
          translationKey={`project:animal-scale-${props.scale}-volume`}
          variant="caption"
          component="p"
        >
          {meta.volume}
        </LocalizedTypography>
        <LocalizedTypography
          translationKey={`project:animal-scale-${props.scale}-edge`}
          variant="caption"
          component="p"
        >
          {meta.edge}
        </LocalizedTypography>
        <LocalizedTypography
          translationKey={`project:animal-scale-${props.scale}-examples`}
          variant="caption"
          component="p"
          color="textSecondary"
        >
          {meta.examples}
        </LocalizedTypography>
      </div>
    </Box>
  )

  return (
    <Tooltip
      title={titleNode}
      arrow
      placement="left"
      {...(props.tooltipProps || {})}
      slotProps={{
        tooltip: {
          sx: (theme) => ({
            backgroundColor: theme.palette.background.paper,
            border: `1px solid ${theme.palette.divider}`,
            color: theme.palette.text.primary,
          }),
        },
      }}
    >
      <span
        style={{
          display: 'inline-flex',
          lineHeight: 0,
          height: '100%',
          width: '100%',
        }}
      >
        <Icon />
      </span>
    </Tooltip>
  )
}
